import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined'
import {
  Alert,
  Avatar,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { useState } from 'react'

import { Box } from '@/components/ui'
import type { StaffListItem } from '@/definitions/staff'
import { useConfirm } from '@/hooks/useConfirm'
import { useSetStaffEnabled } from '@/hooks/useSetStaffEnabled'
import { useStaff } from '@/hooks/useStaff'
import { useStaffDetail } from '@/hooks/useStaffDetail'
import { PageCondensed } from '@/layouts'

import { useRouteOrg } from '../../orgRoute'
import { StaffCard } from './components/StaffCard'
import { buildStaffRows, isOnlyActiveAdmin } from './utils/buildStaffRows'
import {
  filterStaffRows,
  STAFF_STATUS_FILTERS,
  type StaffStatusFilter,
} from './utils/filterStaffRows'
import { staffCountLabel } from './utils/staffCountLabel'
import {
  staffDeactivationCopy,
  staffEnabledActionLabel,
  staffEnabledSuccessMessage,
} from './utils/staffEnabledCopy'

const STAFF_UNREADABLE_MESSAGE =
  'This organization’s staff could not be loaded. Reload to try again.'

const NO_STAFF_MESSAGE =
  'This organization has no staff yet, so nobody can sign in to it. Its first ' +
  'administrator is created as part of setting the organization up — use Resume Setup ' +
  'on the Overview tab to add them. Every staff member after that is added by the ' +
  'practice itself in Bartimaeus.'

const NO_MATCHING_STAFF_MESSAGE =
  'This organization has staff, but none with the status you are filtering for.'

/**
 * An organization's staff.
 *
 * The list response carries the name, email, role, and account state, so the
 * whole list renders from one request; a staff member's remaining fields are
 * fetched only when their card is opened. One card is open at a time, which is
 * what keeps that to a single detail request rather than one per member.
 *
 * Creating and editing staff is not offered: the first administrator comes from
 * the organization setup flow and the practice's own admin manages the rest in
 * Bartimaeus. Two things are the exception, because the practice cannot do either
 * for itself: withdrawing someone's access, whose confirmation and request are
 * owned here rather than by the card, and switching a staff member's E-Prescribe,
 * which the open card owns because both the clinician link it collects and the
 * failures it reports belong to that one staff member.
 */
export const StaffTab = () => {
  const org = useRouteOrg()
  const [openStaffId, setOpenStaffId] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<StaffStatusFilter>('all')

  const confirm = useConfirm()
  const { data: staff, isLoading, isError, isFetching } = useStaff(org.id)

  const rows = buildStaffRows(staff ?? [])
  const visibleRows = filterStaffRows(rows, statusFilter)

  // A row the filter no longer shows has no card to fill, so the detail request
  // behind it stops rather than being left running for a row nobody can see. Read
  // off the list, because a status change and a filter change both take a row out.
  const isOpenRowVisible = visibleRows.some((row) => row.staff.id === openStaffId)
  const detail = useStaffDetail(org.id, openStaffId ?? '', isOpenRowVisible)

  const { mutate: setStaffEnabled, isPending, variables } = useSetStaffEnabled()

  // One mutation serves every card, so which card shows progress comes from the
  // request in flight rather than from the hook being busy at all.
  const pendingStaffId = isPending ? variables.staffId : null
  // Held until the refreshed list arrives, not just until the response does: the
  // status chip and the action's own label are what say the change landed, and
  // both wait on the refetch.
  const isActionDisabled = isPending || isFetching

  const handleSetEnabled = async (member: StaffListItem, enabled: boolean) => {
    if (!enabled) {
      const copy = staffDeactivationCopy(member, isOnlyActiveAdmin(rows, member.id))
      const confirmed = await confirm({
        title: copy.title,
        body: (
          <Box variant='flexColumn' gap='md'>
            <Alert severity='warning'>
              {copy.effect}
              {copy.lastAdminNote !== undefined && ` ${copy.lastAdminNote}`}
            </Alert>
            <Typography variant='body2' color='text.secondary'>
              {copy.reversal}
            </Typography>
          </Box>
        ),
        confirmLabel: staffEnabledActionLabel(true),
        destructive: true,
      })
      if (!confirmed) {
        return
      }
    }

    setStaffEnabled({
      orgId: org.id,
      staffId: member.id,
      enabled,
      successMessage: staffEnabledSuccessMessage(member, enabled),
    })
  }

  const hasNoStaff = !isLoading && !isError && rows.length === 0
  // Distinct from having no staff at all: the organization has people, the
  // chosen status just excludes all of them. Saying "no staff yet" here would
  // be false.
  const hasNoMatchingStaff = rows.length > 0 && visibleRows.length === 0
  // Withheld until the roster is known, so a count does not appear next to a
  // spinner or next to the message saying the list could not be read.
  const countLabel =
    isLoading || isError ? '' : staffCountLabel(visibleRows.length, rows.length)

  return (
    <PageCondensed>
      <Box variant='flexColumn' gap='lg'>
        <Box variant='flexBetween' gap='md'>
          <Box variant='flex' gap='md'>
            <Avatar variant='titleIcon'>
              <PeopleOutlinedIcon />
            </Avatar>
            <Box variant='flexColumn' gap='none' fullWidth={false}>
              <Typography variant='h4' fontWeight={600}>
                Staff
              </Typography>
              {countLabel !== '' && (
                <Typography variant='body2' color='text.secondary'>
                  {countLabel}
                </Typography>
              )}
            </Box>
          </Box>
          {/* Offered only once there is a roster to narrow. */}
          {rows.length > 0 && (
            <ToggleButtonGroup
              exclusive
              value={statusFilter}
              aria-label='Filter staff by status'
              // Null when the pressed segment was already the chosen one, which
              // would otherwise clear the filter and leave no segment selected.
              onChange={(_event, value: StaffStatusFilter | null) =>
                value !== null && setStatusFilter(value)
              }
            >
              {STAFF_STATUS_FILTERS.map((option) => (
                <ToggleButton key={option.value} value={option.value}>
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}
        </Box>

        {isLoading && (
          <Box variant='flexCenter'>
            <CircularProgress />
          </Box>
        )}

        {isError && <Alert severity='error'>{STAFF_UNREADABLE_MESSAGE}</Alert>}

        {hasNoStaff && (
          <Typography variant='body2' color='text.secondary'>
            {NO_STAFF_MESSAGE}
          </Typography>
        )}

        {hasNoMatchingStaff && (
          <Typography variant='body2' color='text.secondary'>
            {NO_MATCHING_STAFF_MESSAGE}
          </Typography>
        )}

        {visibleRows.length > 0 && (
          <Box variant='flexColumn' gap='md'>
            {visibleRows.map((row) => {
              const isOpen = openStaffId === row.staff.id

              return (
                <StaffCard
                  key={row.staff.id}
                  row={row}
                  isOpen={isOpen}
                  onToggle={() => setOpenStaffId(isOpen ? null : row.staff.id)}
                  detail={isOpen ? detail.data : undefined}
                  isDetailLoading={isOpen && detail.isLoading}
                  isDetailError={isOpen && detail.isError}
                  isActionPending={pendingStaffId === row.staff.id}
                  isActionDisabled={isActionDisabled}
                  onSetEnabled={(enabled) => handleSetEnabled(row.staff, enabled)}
                  orgId={org.id}
                  isOrgErxEnabled={org.erx_enabled === true}
                  // The detail query rather than the list: E-Prescribe is read off
                  // the opened record, so that is the one whose refresh the switch
                  // has to wait for.
                  isRefreshing={isOpen && detail.isFetching}
                />
              )
            })}
          </Box>
        )}
      </Box>
    </PageCondensed>
  )
}
